<template>
  <section ref="screenRoot" class="page admin-screen" data-test="admin-users">
    <div class="anim" data-delay="80">
      <Eyebrow>Admin · access control</Eyebrow>
      <DisplayHeading lead="Connected" accent="users" />
      <p class="display-sub">
        Every Foodics teammate who's signed in via Google. Edit each row to
        change role or restrict which projects they can see — `*` means
        every project.
      </p>
      <AdminTabs />
    </div>

    <div v-if="loadError" class="error-state anim" data-delay="160">
      <p class="error-title">Couldn't load users</p>
      <p class="error-msg">{{ loadError }}</p>
      <button class="retry" type="button" @click="load(true)">Retry</button>
    </div>

    <div v-else-if="loading && users.length === 0" class="skeletons anim" data-delay="160">
      <div v-for="i in 4" :key="i" class="skel-row" />
    </div>

    <div v-else class="users-table anim" data-delay="160">
      <header class="users-head">
        <span class="lbl-mono">User</span>
        <span class="lbl-mono">Role</span>
        <span class="lbl-mono">Projects</span>
        <span class="lbl-mono">First seen</span>
        <span class="lbl-mono">Last modified</span>
        <span aria-hidden="true" />
      </header>

      <article
        v-for="u in users"
        :key="u.username"
        class="user-row"
        :class="{ open: editing === u.username }"
        :data-test="`user-row-${u.username}`"
      >
        <div class="user-cell user">
          <div class="email">
            {{ u.email }}
            <span v-if="u.email === currentEmail" class="me-tag">you</span>
          </div>
          <div class="meta">{{ u.username }} · {{ u.status.toLowerCase().replace(/_/g, " ") }}</div>
        </div>

        <div class="user-cell">
          <span :class="['role-pill', u.role]" :title="ROLE_DESCRIPTION[u.role]">{{ u.role }}</span>
        </div>

        <div class="user-cell projects">
          <span v-if="u.role === 'admin' || u.role === 'viewer'" class="all-projects">
            all projects
            <span class="all-source">via {{ u.role }}</span>
          </span>
          <template v-else>
            <span v-if="u.projects.includes('*')" class="all-projects">all projects</span>
            <span v-for="p in u.projects.filter((x) => x !== '*')" :key="p" class="proj-chip">{{ p }}</span>
          </template>
        </div>

        <div class="user-cell mono small">{{ formatDate(u.first_seen) }}</div>
        <div class="user-cell mono small">{{ formatDate(u.last_modified) }}</div>

        <div class="user-cell action">
          <button
            type="button"
            class="ghost-btn"
            :data-test="`edit-${u.username}`"
            @click="toggleEdit(u)"
          >{{ editing === u.username ? "Close" : "Edit" }}</button>
        </div>

        <form
          v-if="editing === u.username"
          class="edit-row"
          :data-test="`edit-form-${u.username}`"
          @submit.prevent="save(u)"
        >
          <div class="field">
            <label>Role</label>
            <select v-model="draft.role" :data-test="`role-${u.username}`">
              <option value="viewer">viewer — sees all projects</option>
              <option value="user">user — restricted by allowlist</option>
              <option value="admin">admin — sees all + manages users</option>
            </select>
            <p class="field-hint">{{ ROLE_DESCRIPTION[draft.role] }}</p>
          </div>

          <div v-if="draft.role === 'user'" class="field projects-field">
            <label>Allowed projects</label>
            <div class="projects-chooser">
              <label class="check">
                <input
                  type="checkbox"
                  :checked="draftWildcard"
                  :data-test="`wildcard-${u.username}`"
                  @change="setWildcard(($event.target as HTMLInputElement).checked)"
                />
                <span>All projects (<code>*</code>) — equivalent to viewer</span>
              </label>
              <div v-if="!draftWildcard" class="projects-grid">
                <label
                  v-for="key in availableProjectKeys"
                  :key="key"
                  class="check"
                >
                  <input
                    type="checkbox"
                    :value="key"
                    :checked="draft.projects.includes(key)"
                    :data-test="`project-${u.username}-${key}`"
                    @change="toggleProject(key, ($event.target as HTMLInputElement).checked)"
                  />
                  <span>{{ key }}</span>
                </label>
              </div>
            </div>
          </div>
          <p v-else class="projects-note">
            {{ draft.role === "admin" ? "Admins" : "Viewers" }} see every project — no per-project allowlist.
          </p>

          <div v-if="saveError" class="save-error">{{ saveError }}</div>

          <div class="actions">
            <button type="button" class="ghost-btn" @click="cancel">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving" :data-test="`save-${u.username}`">
              {{ saving ? "Saving…" : "Save" }}
            </button>
          </div>
        </form>
      </article>
    </div>

    <div class="footnote anim" data-delay="400">
      <span>{{ users.length }} user{{ users.length === 1 ? "" : "s" }}</span>
      <span class="dotsep" />
      <span>Bootstrap admin: amine.aitelharraj@foodics.com</span>
      <span class="dotsep" />
      <span>Cognito pool eu-west-1_viderTF9S</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import AdminTabs from "@/features/admin/AdminTabs.vue";
import { useReveal } from "@/composables/useReveal";
import { useApi } from "@/composables/useApi";
import { listUsers, patchUser, ROLE_DESCRIPTION, type AdminUser, type Role } from "@/api/admin";
import { useProjectsStore } from "@/stores/projects";
import { useAuthStore } from "@/stores/auth";

const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const auth = useAuthStore();
const currentEmail = computed(() => auth.email);
const projectsStore = useProjectsStore();

const users = ref<AdminUser[]>([]);
const loading = ref(false);
const loadError = ref<string>("");

const editing = ref<string | null>(null);
const draft = reactive<{ role: Role; projects: string[] }>({ role: "viewer", projects: ["*"] });
const draftWildcard = computed(() => draft.projects.includes("*"));
const saving = ref(false);
const saveError = ref<string>("");

const { client } = useApi();

const availableProjectKeys = computed<string[]>(() => {
  const fromStore = projectsStore.items.map((p) => p.key);
  // Always offer the project keys we already know about, plus any that
  // appear on existing users (so we don't accidentally drop a user's
  // assignment because the project list happened to be cold).
  const fromUsers = users.value.flatMap((u) => u.projects).filter((k) => k !== "*");
  return Array.from(new Set([...fromStore, ...fromUsers])).sort();
});

async function load(force = false): Promise<void> {
  if (force) loadError.value = "";
  loading.value = true;
  try {
    users.value = await listUsers(client);
    // Cold-start the projects list so the chip chooser is populated even
    // when an admin lands on /admin first.
    if (projectsStore.items.length === 0) void projectsStore.load();
  } catch (err: unknown) {
    const e = err as { status?: number; message?: string };
    loadError.value = e.message || `Error ${e.status ?? "unknown"}`;
  } finally {
    loading.value = false;
  }
}

function toggleEdit(u: AdminUser): void {
  if (editing.value === u.username) {
    editing.value = null;
    return;
  }
  editing.value = u.username;
  saveError.value = "";
  draft.role = u.role;
  draft.projects = [...u.projects];
}

function cancel(): void {
  editing.value = null;
  saveError.value = "";
}

function setWildcard(on: boolean): void {
  draft.projects = on ? ["*"] : [];
}

function toggleProject(key: string, on: boolean): void {
  if (on) {
    if (!draft.projects.includes(key)) draft.projects = [...draft.projects.filter((p) => p !== "*"), key];
  } else {
    draft.projects = draft.projects.filter((p) => p !== key);
  }
  if (draft.projects.length === 0) draft.projects = ["*"];
}

async function save(u: AdminUser): Promise<void> {
  saveError.value = "";
  saving.value = true;
  try {
    const patch: Partial<Pick<AdminUser, "role" | "projects">> = {};
    if (draft.role !== u.role) patch.role = draft.role;
    // Admins and viewers always see everything — clobber the allowlist to
    // ['*'] so the stored attribute matches the role's semantics. Skipping
    // this would leave a stale allowlist on disk that becomes confusing
    // if the user is later demoted back to `user`.
    const desiredProjects = draft.role === "user" ? draft.projects : ["*"];
    if (
      desiredProjects.length !== u.projects.length
      || desiredProjects.some((p, i) => p !== u.projects[i])
    ) {
      patch.projects = desiredProjects;
    }
    if (Object.keys(patch).length === 0) {
      editing.value = null;
      return;
    }
    const updated = await patchUser(client, u.username, patch);
    const idx = users.value.findIndex((x) => x.username === u.username);
    if (idx >= 0) users.value[idx] = updated;
    editing.value = null;
  } catch (err: unknown) {
    const e = err as { status?: number; message?: string };
    saveError.value = `${e.message || "Save failed"} (HTTP ${e.status ?? "?"})`;
  } finally {
    saving.value = false;
  }
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

onMounted(() => { void load(); });
</script>

<style scoped>
.admin-screen { position: relative; }

.error-state {
  margin-top: 24px;
  padding: 16px;
  background: var(--red-soft);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  border-radius: 12px;
  color: var(--red-ink);
  display: grid; gap: 8px;
}
.error-title { font-weight: 500; margin: 0; }
.error-msg { font-size: 13px; margin: 0; color: var(--ink-2); }
.retry {
  align-self: start;
  background: var(--bg-elev);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--red-ink);
}

.skeletons { display: grid; gap: 12px; margin-top: 24px; }
.skel-row {
  height: 64px; border-radius: 12px;
  background: var(--skeleton);
  background-size: 200% 100%;
  animation: shimmer 1.6s linear infinite;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.users-table {
  margin-top: 24px;
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
}

.users-head, .user-row {
  display: grid;
  grid-template-columns: 2.4fr 100px 2fr 1.2fr 1.2fr 88px;
  gap: 16px;
  align-items: center;
  padding: 14px 24px;
}
.users-head {
  background: var(--bg-tint);
  border-bottom: 1px solid var(--line);
}
.user-row {
  border-top: 1px solid var(--line-2);
  position: relative;
  transition: background var(--speed-quick) var(--ease);
}
.user-row:hover { background: color-mix(in srgb, var(--bg-tint) 30%, transparent); }
.user-row.open { background: var(--brand-tint); }

.user-cell { min-width: 0; }
.user .email {
  font-size: 14px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 8px;
}
.me-tag {
  font-family: "JetBrains Mono", monospace;
  font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase;
  background: var(--brand-soft);
  color: var(--brand);
  padding: 1px 6px;
  border-radius: 4px;
}
.user .meta {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  color: var(--muted-2);
  margin-top: 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.role-pill {
  display: inline-block;
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 999px;
}
.role-pill.admin {
  background: var(--brand-soft);
  color: var(--brand);
}
.role-pill.viewer {
  background: var(--green-soft);
  color: var(--green-ink);
}
.role-pill.user {
  background: var(--bg-tint);
  color: var(--ink-2);
}

.projects { display: flex; flex-wrap: wrap; gap: 6px; }
.all-projects {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.04em;
  color: var(--ink-2);
  background: var(--bg-tint);
  padding: 2px 8px;
  border-radius: 999px;
  display: inline-flex; align-items: center; gap: 6px;
}
.all-source {
  font-size: 9px; opacity: 0.6;
  letter-spacing: 0.12em;
}
.proj-chip {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.04em;
  background: var(--brand-soft);
  color: var(--brand);
  padding: 2px 8px;
  border-radius: 999px;
}

.mono.small {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  color: var(--muted);
}

.action { text-align: right; }

.ghost-btn {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-2);
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  transition: border-color var(--speed-quick) var(--ease),
              transform var(--speed-quick) var(--ease);
}
.ghost-btn:hover { border-color: var(--ink); transform: translateY(-1px); }
.ghost-btn:focus-visible {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}

.edit-row {
  grid-column: 1 / -1;
  display: grid; gap: 18px;
  margin-top: 14px;
  padding: 18px 20px;
  border-top: 1px dashed var(--line);
  background: var(--bg-tint);
  border-radius: 12px;
}
.field {
  display: grid; gap: 6px;
}
.field label {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--muted);
}
.field select {
  font-family: inherit; font-size: 14px;
  padding: 8px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--bg-elev);
  color: var(--ink);
  width: fit-content;
}
.field select:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}
.field-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.4;
  max-width: 480px;
}
.projects-note {
  margin: 0;
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.08em;
  color: var(--muted);
  background: var(--bg-elev);
  border: 1px dashed var(--line);
  padding: 10px 14px;
  border-radius: 8px;
}

.projects-field { gap: 10px; }
.projects-chooser { display: grid; gap: 12px; }
.check {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}
.check input[type="checkbox"] { accent-color: var(--brand); }
.check code {
  font-family: "JetBrains Mono", monospace;
  background: var(--bg-elev);
  padding: 1px 5px;
  border-radius: 4px;
}
.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px 16px;
  padding: 10px 12px;
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 8px;
}

.save-error {
  font-size: 13px;
  color: var(--red-ink);
  background: var(--red-soft);
  border-radius: 8px;
  padding: 8px 12px;
}

.actions {
  display: flex; justify-content: flex-end;
  gap: 8px;
}
.actions .btn-primary { padding: 8px 16px; font-size: 13px; }
.actions .btn-primary:disabled {
  opacity: 0.6; cursor: not-allowed;
  transform: none; box-shadow: none;
}

@media (max-width: 1100px) {
  .users-head { display: none; }
  .user-row {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "user      role"
      "projects  projects"
      "first     last"
      "action    action";
    gap: 8px 12px;
  }
  .user-row > .user-cell.user { grid-area: user; }
  .user-row > .user-cell:nth-child(2) { grid-area: role; justify-self: end; }
  .user-row > .user-cell.projects { grid-area: projects; }
  .user-row > .user-cell.mono.small:nth-of-type(4) { grid-area: first; }
  .user-row > .user-cell.mono.small:nth-of-type(5) { grid-area: last; text-align: right; }
  .user-row > .user-cell.action { grid-area: action; }
}
</style>
